import * as crossfilter from "crossfilter2";
import * as d3 from "d3";
import * as dc from "dc";
import * as ko from "knockout";
import * as moment from "moment";
import * as constants from "../common/constants";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as appointmentDataModule from "../data/appointmentData";
import { IController } from "../interfaces/controller";
import * as User from "../interfaces/user";

interface IAppointmentData extends appointmentDataModule.IAppointmentCountDashboardItem  {
    AppointmentDateAsDate?: Date | null;
}

export class DashboardController implements IController {
    // references to DOM elements
    private container: JQuery;
    private viewModel: any = {};
    private numberOfAppointmentsByUserChart: dc.PieChart;
    private numberOfAppointmentsByTemplateChart: dc.RowChart;
    private numberOfAppointmentsByDay: dc.LineChart;
    // private appointmentsByStateMap: dc.GeoChoroplethChart;

    constructor() {
        const me: DashboardController = this;
        // const app: IApplication = Global.app();

        this.viewModel = {
            isErrorLoadingAppointments: ko.observable<boolean>(false),
            resetNumberOfAppointmentsByUser(): void {
                me.numberOfAppointmentsByUserChart.filterAll();
                dc.redrawAll();
            },
            resetNumberOfAppointmentsByTemplate(): void {
                me.numberOfAppointmentsByTemplateChart.filterAll();
                dc.redrawAll();
            },
            resetNumberOfAppointmentsByDay(): void {
                me.numberOfAppointmentsByDay.filterAll();
                dc.redrawAll();
            },
            // resetNumberOfAppointmentsByState(): void {
            //     me.appointmentsByStateMap.filterAll();
            //     dc.redrawAll();
            // },

            topMenu: ko.observableArray(),
            onMenuItemClick(menuItem: User.IMenuItem): void {
                Global.app().router().navigate(menuItem.URL, true);
            },
        };
    }

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;
        ko.applyBindings(this.viewModel, this.container[0]);

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.loadStats();

        const userSession = Global.app().userSession;
        if (userSession) {
            this.viewModel.topMenu(userSession.ApplicationNavigation.TopMenu);
        }

        deferred.resolve();
        return deferred.promise();
    }

    public haveFieldsChanged(): boolean {
        // no editable fields
        return false;
    }

    public appRouterID(params: any[]): string {
        return "Dashboard";
    }

    // returns a title for the page
    public title(): string {
        return "Dashboard";
    }

    public subTitle(): string {
        return "";
    }

    private loadStats() {
        const waitContainer = UI.showWaitIndicator(this.container);

        // Hard coded to do 30 days in the past and future.
        // Feature development may add controls for user to select date range.
        const request: appointmentDataModule.IAppointmentCountForDashboardRequest = {
            startDate: moment().add(-30, "days").format(constants.MOMENT_FORMAT_DATE),
            endDate: moment().add(30, "days").format(constants.MOMENT_FORMAT_DATE),
        };

        appointmentDataModule.GetForDashboardCount(request)
        .done((response) => {
            if (response.Success) {
                this.viewModel.isErrorLoadingAppointments(false);
                this.loadChartControls(response.Payload);
            } else {
                this.viewModel.isErrorLoadingAppointments(true);
            }
        })
        .fail(() => {
            this.viewModel.isErrorLoadingAppointments(true);
        })
        .always(() => {
            UI.hideWaitIndicator(this.container, waitContainer);
        });
    }

    private loadChartControls(appointmentData: IAppointmentData[]) {
        this.numberOfAppointmentsByUserChart = dc.pieChart("#number-of-appointments-by-user-chart");
        this.numberOfAppointmentsByTemplateChart = dc.rowChart("#number-of-appointments-by-template-chart");
        this.numberOfAppointmentsByDay = dc.lineChart("#number-of-appointments-by-day-chart");
        // this.appointmentsByStateMap = dc.geoChoroplethChart("#number-of-appointments-by-state-map");

        if (!appointmentData || appointmentData.length === 0) {
            // no stats to process, can happen with a brand new customer
            return;
        }

        // normalize/parse data
        const parseDate = d3.timeParse("%Y-%m-%d");

        appointmentData.forEach((d) => {
            d.AppointmentDateAsDate = parseDate(d.AppointmentDate);
        });

        // set crossfilter
        const ndx = crossfilter(appointmentData);
        const assignedUserNameDim = ndx.dimension((d) => d.AssignedUserName);
        const numberOfAppointmentsPerAssignedUserName = assignedUserNameDim.group().reduceSum((d) => +d.NumberOfAppointments);

        const appointmentTemplateNameDim = ndx.dimension((d) => d.AppointmentTemplateName);
        const numberOfAppointmentsPerTemplateName = appointmentTemplateNameDim.group().reduceSum((d) => +d.NumberOfAppointments);

        const appointmentDateDim = ndx.dimension((d) => d.AppointmentDateAsDate || new Date());
        const minDate = appointmentDateDim.bottom(1)[0].AppointmentDateAsDate || new Date();
        const maxDate = appointmentDateDim.top(1)[0].AppointmentDateAsDate || new Date();
        const numberOfAppointmentsPerDay = appointmentDateDim.group().reduceSum((d) => +d.NumberOfAppointments);

        // const stateDim = ndx.dimension((d) => d.State);
        // const numberOfAppointmentsPerState = stateDim.group().reduceSum((d) => +d.NumberOfAppointments);

        this.numberOfAppointmentsByUserChart
            .height(300)
            .dimension(assignedUserNameDim)
            .group(numberOfAppointmentsPerAssignedUserName)
            .innerRadius(50)
            .controlsUseVisibility(true);

        this.numberOfAppointmentsByTemplateChart
            .height(500)
            .dimension(appointmentTemplateNameDim)
            .group(numberOfAppointmentsPerTemplateName)
            .elasticX(true)
            .controlsUseVisibility(true);

        this.numberOfAppointmentsByDay
            .renderArea(true)
            .height(300)
            .elasticY(true)
            .dimension(appointmentDateDim)
            .group(numberOfAppointmentsPerDay)
            .yAxisLabel("Number of Tasks")
            .x(d3.scaleTime<Date>().domain([minDate, maxDate]))
            .xAxis()
            .tickFormat(d3.timeFormat("%m-%d-%y"));

        function show_empty_message(chart: any) {
            const isEmpty = d3.sum(chart.group().all().map(chart.valueAccessor())) === 0;
            const data = isEmpty ? [1] : [];
            const empty = chart.svg().selectAll(".empty-message").data(data);
            empty.enter().append("text")
                .text("NO DATA!")
                .attr({
                    "text-anchor": "middle",
                    "alignment-baseline": "middle",
                    "class": "empty-message",
                    "x": chart.margins().left + chart.effectiveWidth() / 2,
                    "y": chart.margins().top + chart.effectiveHeight() / 2,
                })
                .style("opacity", 0);
            empty.transition().duration(1000).style("opacity", 1);
            empty.exit().remove();
        }
        this.numberOfAppointmentsByTemplateChart.on("pretransition", show_empty_message);
        this.numberOfAppointmentsByDay.on("pretransition", show_empty_message);
        // $.getJSON("./data-files/us-states.json").then((statesJson: any) => {
        //     this.appointmentsByStateMap.width(990)
        //         .height(500)
        //         .dimension(stateDim)
        //         .group(numberOfAppointmentsPerState)
        //         .controlsUseVisibility(true)
        //         .overlayGeoJson(statesJson.features, "state", (d) => d.properties.name);

        //     dc.renderAll();
        // });
        dc.renderAll();
    }

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/dashboard.ts